import { DocumentCategoryDto } from '@cradua/data';
import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';

import * as DocumentCategoryActions from './document-category.actions';

export const DOCUMENT_CATEGORY_FEATURE_KEY = 'documentcategory';

export interface DocumentCategoryState
  extends EntityState<DocumentCategoryDto> {
  selectedDocumentCategoryId?: string | number; // which DocumentCategory record has been selected
  loaded: boolean; // has the DocumentCategory list been loaded
  error?: string | null; // last none error (if any)
}

export interface DocumentCategoryPartialState {
  readonly [DOCUMENT_CATEGORY_FEATURE_KEY]: DocumentCategoryState;
}

export const documentCategoryAdapter: EntityAdapter<DocumentCategoryDto> = createEntityAdapter<
  DocumentCategoryDto
>({
  selectId: (documentCategory) => documentCategory.id,
  sortComparer: false,
});

export const initialState: DocumentCategoryState = documentCategoryAdapter.getInitialState(
  {
    // set initial required properties
    loaded: false,
    error: null,
    selectedDocumentCategoryId: null,
  }
);

const documentCategoryReducer = createReducer(
  initialState,
  on(DocumentCategoryActions.loadDocumentCategory, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    DocumentCategoryActions.loadDocumentCategorySuccess,
    (state, { documentCategory }) =>
      documentCategoryAdapter.addAll(documentCategory, {
        ...state,
        loaded: false,
      })
  ),
  on(
    DocumentCategoryActions.loadDocumentCategoryFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(DocumentCategoryActions.addDocumentCategory, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    DocumentCategoryActions.addDocumentCategorySuccess,
    (state, { newDocumentCategory }) =>
      documentCategoryAdapter.addMany(newDocumentCategory, {
        ...state,
        loaded: false,
      })
  ),
  on(
    DocumentCategoryActions.addDocumentCategoryFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(DocumentCategoryActions.updateDocumentCategory, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(
    DocumentCategoryActions.updateDocumentCategorySuccess,
    (state, { updateDocumentCategory }) =>
      documentCategoryAdapter.upsertMany(updateDocumentCategory, {
        ...state,
        loaded: false,
      })
  ),
  on(
    DocumentCategoryActions.updateDocumentCategoryFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  ),
  on(DocumentCategoryActions.removeDocumentCategory, (state) => ({
    ...state,
    loaded: true,
    error: null,
  })),
  on(DocumentCategoryActions.removeDocumentCategorySuccess, (state, { ids }) =>
    documentCategoryAdapter.removeMany(ids, { ...state, loaded: false })
  ),
  on(
    DocumentCategoryActions.removeDocumentCategoryFailure,
    (state, { error }) => ({
      ...state,
      loaded: false,
      error,
    })
  )
);

export function reducer(
  state: DocumentCategoryState | undefined,
  action: Action
) {
  return documentCategoryReducer(state, action);
}
